import React, { useEffect, useState } from "react";
import { Button, ButtonSize } from "../../../../components/Button";
import { ButtonVariant } from "../../../../config/colors";
import { useNavigate, useLocation } from "react-router-dom";
import { useWallet } from "../../../../hooks/useWallet";
import LnUrl from "./LnUrl";
import { formatAmountWithFIAT, formatAmountWithSATS } from "../../../../utils/Utils";
import Clickable from "../../../../components/Clickable";
import { CopyToClipboard } from "../../../../components/CopyToClipboard";
import { ButtonAlignement } from "../../../../components/ConfirmModal";
import { useConfirm } from "../../../../hooks/useConfirm";
import Loader from "../../components/Loader";
import { CurrencyType } from "../../../../store/slices/walletSlice";
import { WalletType } from "../../../../models/Transactions";


interface WalletInfosProps {
  
}

const WalletInfos: React.FC<WalletInfosProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { balance, lnurl, regenerateNWCConnectionString, nwcConnectionString, currencyType, balanceFiat, setCurrencyType, currencyCodeFiat, walletType } = useWallet();
    const [showNWCConnectionString, setShowNWCConnectionString] = useState(false);
    const [isLoadingNWCConnectionString, setIsLoadingNWCConnectionString] = useState(false);
    const { openConfirm } = useConfirm();

    useEffect(() => {
        if(nwcConnectionString) {
            setIsLoadingNWCConnectionString(false);
        }
    }, [nwcConnectionString]);

    const actionRegenerateNWCConnectionString = () => {
        setIsLoadingNWCConnectionString(true);
        regenerateNWCConnectionString();
    }

    const renderBalance = () => {
        if(currencyType === CurrencyType.FIAT && balanceFiat !== null && currencyCodeFiat !== null) {
            return formatAmountWithFIAT(balanceFiat, currencyCodeFiat);
        }
        return formatAmountWithSATS(balance ?? 0, true);
    }

    return (
        <div 
            className="flex-shrink-0 bg-brand-bg-4 rounded-xl p-4 flex flex-col relative overflow-hidden"
        >
            <div className={`relative z-10 flex flex-col gap-3 ${balance !== null ? '' : 'invisible'}`}>
                <div onClick={() => {
                    setCurrencyType(currencyType === CurrencyType.SATS ? CurrencyType.FIAT : CurrencyType.SATS);
                }} className="cursor-pointer">
                    <div className="text-3xl font-semibold">{renderBalance()}</div>
                </div>
                <LnUrl lnurl={lnurl} />
                <div className="flex flex-row gap-2 mt-4">
                    <Button 
                        variant={ButtonVariant.inverted} 
                        size={ButtonSize.medium} 
                        className="flex-1 w-[50%]"
                        onClick={() => {
                            navigate('/home/wallet/receive');
                        }}
                    >
                        <div className="flex flex-row items-center justify-center flex-1 gap-2">
                            <div className="w-[20px] flex items-center justify-center">
                                <span className="material-symbols-outlined">
                                    call_received
                                </span>
                            </div>
                            <div>Receive</div>
                        </div>
                    </Button>
                    <Button 
                        variant={ButtonVariant.primary} 
                        size={ButtonSize.medium} 
                        className="flex-1 w-[50%]"
                        onClick={() => {
                            navigate('/home/wallet/send/recipient', {
                                replace: true,
                                state: { timestamp: Date.now() }
                            });
                        }}
                    >
                        <div className="flex flex-row items-center justify-center flex-1 gap-2">
                            <div>Send</div>
                            <div className="w-[20px] flex items-center justify-center">
                                <span className="material-symbols-outlined">
                                    call_made
                                </span>
                            </div>
                        </div>
                    </Button>
                </div>
                { walletType === WalletType.INTERNAL ? (
                <div className="flex flex-row gap-2 justify-start">
                    {
                        showNWCConnectionString ? (
                            <div className="flex-1 flex flex-col gap-2">
                                {
                                    isLoadingNWCConnectionString ? (
                                        <Loader small />
                                    ) : (
                                        <CopyToClipboard 
                                            toCopy={nwcConnectionString ?? ''} 
                                            className="break-all"
                                            onCopied={() => {
                                                setShowNWCConnectionString(false);
                                            }}
                                        >
                                            {nwcConnectionString}
                                        </CopyToClipboard>
                                    )
                                }
                                
                                <div className="flex flex-row gap-2 justify-between">
                                    <CopyToClipboard 
                                        toCopy={nwcConnectionString ?? ''}
                                        className="py-1 px-2 rounded-full text-[#9B59B6]"
                                        absolute={false}
                                        onCopied={() => {
                                            setShowNWCConnectionString(false);
                                        }}
                                    >
                                        Copy
                                    </CopyToClipboard>
                                    <Clickable
                                        onClick={() => {
                                            openConfirm({
                                                message: 'Are you sure you want to revoke this token ?',
                                                buttons: [
                                                    {label: 'Cancel', onClick: () => {}, variant: ButtonVariant.transparent},
                                                    {label: 'Revoke', onClick: () => {actionRegenerateNWCConnectionString()}, variant: ButtonVariant.danger}
                                                ],
                                                buttonAlignement: ButtonAlignement.row
                                            });
                                        }}
                                        className="py-1 px-2 rounded-full text-brand-red hover:bg-transparent"
                                    >
                                        Revoke
                                    </Clickable>
                                </div>
                            </div>
                        ) : (
                            <Clickable
                                onClick={() => {
                                    setShowNWCConnectionString(true);

                                    if(!nwcConnectionString) {
                                        actionRegenerateNWCConnectionString();
                                    }
                                }}
                                className="py-1 px-2 rounded-full text-[#9B59B6] hover:bg-transparent"
                            >
                                Get NWC Connection
                            </Clickable>
                        )
                     }
                </div>
                ) : null }
                { walletType === WalletType.SPARK ? (
                    <div className="flex flex-col gap-2 justify-start">
                        <Clickable
                            onClick={() => {
                                if (location.pathname === '/home/wallet/seed') {
                                    navigate('/home/wallet');
                                } else {
                                    navigate('/home/wallet/seed');
                                }
                            }}
                            className="px-2 rounded-full hover:bg-transparent"
                        >
                            View Seed
                        </Clickable>
                    </div>
                    ) : null 
                }
            </div>
            <div 
                className="absolute inset-0 z-0"
                style={{
                    background: 'url("/images/wallet_bg.png") no-repeat center center',
                    backgroundSize: '100% 100%',
                    opacity: 0.25,
                }}
            />
        </div>
    )
}

export default WalletInfos;